import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';
import { CNetActions } from '../other/_cnet';

export default function MortgageCalc() {
  const [homePrice, setHomePrice] = useState(400000);
  const [downPayment, setDownPayment] = useState(80000);
  const [term, setTerm] = useState(30);
  const [rate, setRate] = useState(6.625);
  const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const today = new Date();
  const [startMonth, setStartMonth] = useState(today.getMonth() + 1);
  const [startYear, setStartYear] = useState(today.getFullYear());
  const [includeExtras, setIncludeExtras] = useState(true);
  const [propertyTaxes, setPropertyTaxes] = useState(1.2);
  const [propertyTaxMode, setPropertyTaxMode] = useState('%');
  const [insurance, setInsurance] = useState(1500);
  const [pmi, setPmi] = useState(0);
  const [pmiMode, setPmiMode] = useState('%');
  const [hoa, setHoa] = useState(0);
  const [other, setOther] = useState(4000);
  const [showAll, setShowAll] = useState(false);
  const [scheduleMode, setScheduleMode] = useState('yearly'); // 'yearly' | 'monthly'
  const [showResult, setShowResult] = useState(true);
  // More Options panel
  const [showMore, setShowMore] = useState(false);
  const [taxIncrease, setTaxIncrease] = useState(0);
  const [insIncrease, setInsIncrease] = useState(0);
  const [hoaIncrease, setHoaIncrease] = useState(0);
  const [otherIncrease, setOtherIncrease] = useState(0);
  const [extraMonthly, setExtraMonthly] = useState(0);
  const [extraMonthlyStartM, setExtraMonthlyStartM] = useState(today.getMonth() + 1);
  const [extraMonthlyStartY, setExtraMonthlyStartY] = useState(today.getFullYear());
  const [extraYearly, setExtraYearly] = useState(0);
  const [extraYearlyStartM, setExtraYearlyStartM] = useState(today.getMonth() + 1);
  const [extraYearlyStartY, setExtraYearlyStartY] = useState(today.getFullYear());
  const [extraOneTime, setExtraOneTime] = useState(0);
  const [extraOneTimeM, setExtraOneTimeM] = useState(today.getMonth() + 1);
  const [extraOneTimeY, setExtraOneTimeY] = useState(today.getFullYear());
  const [showBiweekly, setShowBiweekly] = useState(false);

  const calc = useMemo(() => {
    const principal = Math.max(0, homePrice - downPayment);
    const monthlyRate = rate / 100 / 12;
    const n = term * 12;
    const monthlyPI = monthlyRate === 0 ? principal / n : (principal * monthlyRate * Math.pow(1 + monthlyRate, n)) / (Math.pow(1 + monthlyRate, n) - 1);
    // Convert extras to monthly $
    const monthlyTax = includeExtras
      ? (propertyTaxMode === '%' ? Number(homePrice) * Number(propertyTaxes) / 100 / 12 : Number(propertyTaxes) / 12)
      : 0;
    const monthlyIns = includeExtras ? Number(insurance) / 12 : 0;
    const monthlyPmi = includeExtras
      ? (pmiMode === '%' ? Number(principal) * Number(pmi) / 100 / 12 : Number(pmi) / 12)
      : 0;
    const monthlyHoa = includeExtras ? Number(hoa) : 0;
    const monthlyOther = includeExtras ? Number(other) / 12 : 0;
    const monthlyExtras = monthlyPmi + monthlyHoa + monthlyOther;
    const monthlyTotal = monthlyPI + monthlyTax + monthlyIns + monthlyExtras;
    const totalPayments = monthlyPI * n;
    const totalInterest = totalPayments - principal;
    // Total out-of-pocket including annual increases
    let totalOutOfPocket = 0;
    for (let y = 0; y < term; y++) {
      const taxFactor = Math.pow(1 + taxIncrease / 100, y);
      const insFactor = Math.pow(1 + insIncrease / 100, y);
      const hoaFactor = Math.pow(1 + hoaIncrease / 100, y);
      const othFactor = Math.pow(1 + otherIncrease / 100, y);
      totalOutOfPocket += (monthlyPI * 12) + (monthlyTax * 12 * taxFactor) + (monthlyIns * 12 * insFactor) + (monthlyPmi * 12 * insFactor) + (monthlyHoa * 12 * hoaFactor) + (monthlyOther * 12 * othFactor);
    }

    let balance = principal;
    let cumulativePayment = 0, cumulativeInterest = 0;
    const schedule = [];
    const monthlySchedule = [];
    for (let year = 1; year <= term; year++) {
      let yearlyInterest = 0, yearlyPrincipal = 0;
      for (let m = 0; m < 12; m++) {
        const interest = balance * monthlyRate;
        let principalPay = monthlyPI - interest;
        yearlyInterest += interest;
        // Apply extra payments
        let extra = extraMonthly;
        const globalMonth = (year - 1) * 12 + m + 1;
        const dt = new Date(startYear, startMonth - 1 + globalMonth - 1, 1);
        const curM = dt.getMonth() + 1, curY = dt.getFullYear();
        // Extra monthly only after start
        if (!((curY > extraMonthlyStartY) || (curY === extraMonthlyStartY && curM >= extraMonthlyStartM))) extra = 0;
        // Extra yearly once per year on/after start
        if ((curY > extraYearlyStartY) || (curY === extraYearlyStartY && curM >= extraYearlyStartM)) {
          if (curM === extraYearlyStartM) extra += extraYearly;
        }
        // Extra one-time
        if (curY === extraOneTimeY && curM === extraOneTimeM) extra += extraOneTime;
        principalPay += extra;
        if (principalPay > balance) principalPay = balance;
        yearlyPrincipal += principalPay;
        balance -= principalPay;
        cumulativePayment += (monthlyPI + extra);
        cumulativeInterest += interest;
        monthlySchedule.push({ month: globalMonth, date: `${MONTHS[dt.getMonth()]}. ${dt.getFullYear()}`, interest, principal: principalPay, balance: Math.max(0, balance), payment: cumulativePayment });
        if (balance <= 0.01) break;
      }
      schedule.push({ year, interest: yearlyInterest, principal: yearlyPrincipal, balance: Math.max(0, balance), payment: cumulativePayment, cumInterest: cumulativeInterest });
      if (balance <= 0.01) break;
    }
    const payoff = new Date(startYear, startMonth - 1 + n, 1);
    // Biweekly payoff simulation: half of monthlyPI every 2 weeks = 26 payments/year
    let biBal = principal, biMonths = 0, biTotalInt = 0;
    if (showBiweekly) {
      const biPmt = monthlyPI / 2;
      const biRate = rate / 100 / 26;
      let periods = 0;
      while (biBal > 0.01 && periods < 5200) {
        const int = biBal * biRate;
        biTotalInt += int;
        const p = Math.min(biBal, biPmt - int);
        biBal -= p;
        periods++;
      }
      biMonths = periods / 26 * 12;
    }
    return { principal, monthlyPI, monthlyTotal, monthlyTax, monthlyIns, monthlyPmi, monthlyHoa, monthlyOther, monthlyExtras, totalPayments, totalInterest, totalOutOfPocket, schedule, monthlySchedule, payoff, biMonths, biTotalInt };
  }, [homePrice, downPayment, term, rate, propertyTaxes, propertyTaxMode, insurance, pmi, pmiMode, hoa, other, includeExtras, startMonth, startYear, taxIncrease, insIncrease, hoaIncrease, otherIncrease, extraMonthly, extraMonthlyStartM, extraMonthlyStartY, extraYearly, extraYearlyStartM, extraYearlyStartY, extraOneTime, extraOneTimeM, extraOneTimeY, showBiweekly]);

  const fmt = n => '$' + n.toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 2 });

  const parts = [
    { label: 'Principal & Interest', v: calc.monthlyPI, color: '#2563eb' },
    { label: 'Property Taxes', v: calc.monthlyTax, color: '#10b981' },
    { label: 'Home Insurance', v: calc.monthlyIns, color: '#f59e0b' },
    { label: 'Other Cost', v: calc.monthlyExtras, color: '#8b5cf6' }
  ].filter(p => p.v > 0);
  const totalPie = parts.reduce((a, b) => a + b.v, 0) || 1;
  let acc = 0;
  const arcs = parts.map(p => {
    const a0 = acc / totalPie * 2 * Math.PI - Math.PI/2; acc += p.v;
    const a1 = acc / totalPie * 2 * Math.PI - Math.PI/2;
    const large = a1 - a0 > Math.PI ? 1 : 0;
    const [x0, y0] = [80 + 70*Math.cos(a0), 80 + 70*Math.sin(a0)];
    const [x1, y1] = [80 + 70*Math.cos(a1), 80 + 70*Math.sin(a1)];
    return { d: `M80,80 L${x0},${y0} A70,70 0 ${large} 1 ${x1},${y1} Z`, color: p.color, label: p.label, v: p.v };
  });

  const resetDefaults = () => {
    setHomePrice(400000); setDownPayment(80000); setTerm(30); setRate(6.5);
    setStartMonth(today.getMonth()+1); setStartYear(today.getFullYear());
    setIncludeExtras(true); setPropertyTaxes(1.2); setPropertyTaxMode('%');
    setInsurance(1500); setPmi(0); setPmiMode('%'); setHoa(0); setOther(4000);
  };

  return (
    <>
      <CalcShell showActions={false} look="cnet" title="Mortgage Calculator" subtitle="Estimate monthly mortgage payments including principal, interest, taxes and insurance."
        about={<>
          <p>A <b>mortgage</b> is a loan used to buy real estate, typically repaid over 15 or 30 years. Monthly payments cover principal, interest, property tax, and insurance (PITI).</p>
          <p>Early payments are mostly interest; over time, more of each payment goes to principal — this is called <b>amortization</b>. Making extra principal payments can significantly reduce total interest paid.</p>
          <p>Lenders typically expect housing costs to be no more than 28% of gross monthly income and total debt no more than 36% (28/36 rule).</p>
        </>}
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div>
            <div className="field"><label>Home Price</label>
              <div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={homePrice} onChange={e => setHomePrice(+e.target.value || 0)} className="flex-1"/></div>
            </div>
            <div className="field"><label>Down Payment</label>
              <div className="grid grid-cols-2 gap-2">
                <div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={downPayment} onChange={e => setDownPayment(+e.target.value || 0)} className="flex-1"/></div>
                <div className="flex gap-2"><input type="number" step="0.1" value={((downPayment/(homePrice||1))*100).toFixed(2)} onChange={e => setDownPayment(Number(e.target.value) * homePrice / 100)} className="flex-1"/><span className="self-center text-slate-500">%</span></div>
              </div>
            </div>
            <div className="field"><label>Loan Term</label>
              <div className="flex gap-2"><input type="number" value={term} onChange={e => setTerm(+e.target.value || 0)} className="flex-1"/><span className="self-center text-slate-500">years</span></div>
            </div>
            <div className="field"><label>Interest Rate</label>
              <div className="flex gap-2"><input type="number" step="0.01" value={rate} onChange={e => setRate(+e.target.value || 0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div>
            </div>
            <div className="field"><label>Start Date</label>
              <div className="grid grid-cols-2 gap-2">
                <select value={startMonth} onChange={e => setStartMonth(+e.target.value)}>{MONTHS.map((m, i) => <option key={m} value={i+1}>{m}</option>)}</select>
                <input type="number" value={startYear} onChange={e => setStartYear(+e.target.value || 0)}/>
              </div>
            </div>
            <label className="flex items-center gap-2 mt-3 mb-3 text-sm font-semibold"><input type="checkbox" checked={includeExtras} onChange={e => setIncludeExtras(e.target.checked)} data-testid="mortgage-include"/> Include Taxes & Costs Below</label>
            {includeExtras && (<>
              <div className="field"><label>Property Taxes (per year)</label>
                <div className="grid grid-cols-2 gap-2">
                  <input type="number" step="0.01" value={propertyTaxes} onChange={e => setPropertyTaxes(+e.target.value || 0)}/>
                  <select value={propertyTaxMode} onChange={e => setPropertyTaxMode(e.target.value)}><option value="%">% of home / yr</option><option value="$">$ / yr</option></select>
                </div>
              </div>
              <div className="field"><label>Home Insurance (per year)</label>
                <div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={insurance} onChange={e => setInsurance(+e.target.value || 0)} className="flex-1"/></div>
              </div>
              <div className="field"><label>PMI Insurance</label>
                <div className="grid grid-cols-2 gap-2">
                  <input type="number" step="0.01" value={pmi} onChange={e => setPmi(+e.target.value || 0)}/>
                  <select value={pmiMode} onChange={e => setPmiMode(e.target.value)}><option value="%">% of loan / yr</option><option value="$">$ / yr</option></select>
                </div>
              </div>
              <div className="field"><label>HOA Fee (per month)</label>
                <div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={hoa} onChange={e => setHoa(+e.target.value || 0)} className="flex-1"/></div>
              </div>
              <div className="field"><label>Other Costs (per year)</label>
                <div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={other} onChange={e => setOther(+e.target.value || 0)} className="flex-1"/></div>
              </div>
            </>)}
            {/* + More Options / − Fewer Options */}
            <button
              type="button"
              onClick={() => setShowMore(v => !v)}
              className="text-blue-700 font-semibold text-sm mt-3 hover:underline"
              data-testid="mortgage-more-options">
              {showMore ? '− Fewer Options' : '+ More Options'}
            </button>
            {showMore && (
              <div className="mt-3 border border-slate-200 rounded p-3 bg-slate-50/60">
                <div className="font-bold text-slate-900 mb-2">Annual Tax & Cost Increase</div>
                <div className="field"><label>Property Taxes Increase</label>
                  <div className="flex gap-2"><input type="number" step="0.01" value={taxIncrease} onChange={e => setTaxIncrease(+e.target.value || 0)} className="flex-1" data-testid="mortgage-tax-increase"/><span className="self-center text-slate-500">%</span></div>
                </div>
                <div className="field"><label>Home Insurance Increase</label>
                  <div className="flex gap-2"><input type="number" step="0.01" value={insIncrease} onChange={e => setInsIncrease(+e.target.value || 0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div>
                </div>
                <div className="field"><label>HOA Fee Increase</label>
                  <div className="flex gap-2"><input type="number" step="0.01" value={hoaIncrease} onChange={e => setHoaIncrease(+e.target.value || 0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div>
                </div>
                <div className="field"><label>Other Costs Increase</label>
                  <div className="flex gap-2"><input type="number" step="0.01" value={otherIncrease} onChange={e => setOtherIncrease(+e.target.value || 0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div>
                </div>
                <div className="font-bold text-slate-900 mb-2 mt-4">Extra Payments</div>
                <div className="field"><label>Extra Monthly Pay</label>
                  <div className="grid grid-cols-[1fr_auto_auto_auto] gap-2 items-center">
                    <div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={extraMonthly} onChange={e => setExtraMonthly(+e.target.value || 0)} className="flex-1" data-testid="mortgage-extra-monthly"/></div>
                    <span className="text-slate-500">from</span>
                    <select value={extraMonthlyStartM} onChange={e => setExtraMonthlyStartM(+e.target.value)}>{MONTHS.map((m, i) => <option key={m} value={i+1}>{m}</option>)}</select>
                    <input type="number" value={extraMonthlyStartY} onChange={e => setExtraMonthlyStartY(+e.target.value || 0)} className="w-20"/>
                  </div>
                </div>
                <div className="field"><label>Extra Yearly Pay</label>
                  <div className="grid grid-cols-[1fr_auto_auto_auto] gap-2 items-center">
                    <div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={extraYearly} onChange={e => setExtraYearly(+e.target.value || 0)} className="flex-1"/></div>
                    <span className="text-slate-500">from</span>
                    <select value={extraYearlyStartM} onChange={e => setExtraYearlyStartM(+e.target.value)}>{MONTHS.map((m, i) => <option key={m} value={i+1}>{m}</option>)}</select>
                    <input type="number" value={extraYearlyStartY} onChange={e => setExtraYearlyStartY(+e.target.value || 0)} className="w-20"/>
                  </div>
                </div>
                <div className="field"><label>Extra One-time Pay</label>
                  <div className="grid grid-cols-[1fr_auto_auto_auto] gap-2 items-center">
                    <div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={extraOneTime} onChange={e => setExtraOneTime(+e.target.value || 0)} className="flex-1"/></div>
                    <span className="text-slate-500">in</span>
                    <select value={extraOneTimeM} onChange={e => setExtraOneTimeM(+e.target.value)}>{MONTHS.map((m, i) => <option key={m} value={i+1}>{m}</option>)}</select>
                    <input type="number" value={extraOneTimeY} onChange={e => setExtraOneTimeY(+e.target.value || 0)} className="w-20"/>
                  </div>
                </div>
                <label className="flex items-center gap-2 mt-3 text-sm font-semibold">
                  <input type="checkbox" checked={showBiweekly} onChange={e => setShowBiweekly(e.target.checked)} data-testid="mortgage-biweekly"/>
                  Show Biweekly Payback Results
                </label>
              </div>
            )}
            <div className="flex gap-2 mt-2">
              <CNetActions onCalc={() => setShowResult(true)} onClear={() => { resetDefaults(); setShowResult(false); }} testId="mortgage" />
            </div>
          </div>
          <div>
            {showResult ? (
              <>
                <div className="result-panel mb-4"><div className="result-title">Monthly Pay</div><div className="result-value" data-testid="mortgage-monthly">{fmt(calc.monthlyTotal)}</div></div>
            {arcs.length > 0 && (
              <div className="flex items-center gap-4 mb-3">
                <svg viewBox="0 0 160 160" className="w-40 h-40 flex-shrink-0">
                  {arcs.map((a, i) => <path key={i} d={a.d} fill={a.color} stroke="#fff" strokeWidth="1"/>)}
                </svg>
                <div className="space-y-1 text-xs">
                  {arcs.map((a, i) => (
                    <div key={i} className="flex items-center gap-2"><span className="w-3 h-3 rounded" style={{background:a.color}}/><span>{a.label}: <b>{fmt(a.v)}</b></span></div>
                  ))}
                </div>
              </div>
            )}
            <table className="data-table">
              <thead><tr><th></th><th className="text-right">Monthly</th><th className="text-right">Total</th></tr></thead>
              <tbody>
                <tr><td>Mortgage Payment</td><td className="text-right font-semibold">{fmt(calc.monthlyPI)}</td><td className="text-right">{fmt(calc.totalPayments)}</td></tr>
                {includeExtras && <tr><td>Property Tax</td><td className="text-right">{fmt(calc.monthlyTax)}</td><td className="text-right">{fmt(calc.monthlyTax * term * 12)}</td></tr>}
                {includeExtras && <tr><td>Home Insurance</td><td className="text-right">{fmt(calc.monthlyIns)}</td><td className="text-right">{fmt(calc.monthlyIns * term * 12)}</td></tr>}
                {includeExtras && calc.monthlyExtras > 0 && <tr><td>Other Costs</td><td className="text-right">{fmt(calc.monthlyExtras)}</td><td className="text-right">{fmt(calc.monthlyExtras * term * 12)}</td></tr>}
                <tr className="border-t-2 font-semibold"><td>Total Out-of-Pocket</td><td className="text-right">{fmt(calc.monthlyTotal)}</td><td className="text-right">{fmt(calc.totalOutOfPocket)}</td></tr>
              </tbody>
            </table>
            <table className="data-table mt-4">
              <tbody>
                <tr><td>House Price</td><td className="text-right font-semibold">{fmt(homePrice)}</td></tr>
                <tr><td>Loan Amount</td><td className="text-right font-semibold">{fmt(calc.principal)}</td></tr>
                <tr><td>Down Payment</td><td className="text-right">{fmt(downPayment)}</td></tr>
                <tr><td>Total of {term * 12} Mortgage Payments</td><td className="text-right">{fmt(calc.totalPayments)}</td></tr>
                <tr><td>Total Interest</td><td className="text-right">{fmt(calc.totalInterest)}</td></tr>
                <tr><td>Mortgage Payoff Date</td><td className="text-right font-semibold">{MONTHS[calc.payoff.getMonth()]}. {calc.payoff.getFullYear()}</td></tr>
              </tbody>
            </table>
              </>
            ) : (
              <div className="text-center text-slate-500 py-8 italic">Click <b>Calculate</b> to see your monthly mortgage payment and full amortization.</div>
            )}
          </div>
        </div>

        {showResult && <StepsPanel steps={[
          `Loan amount = Home price − Down payment = ${fmt(calc.principal)}`,
          `Monthly rate = ${rate}% ÷ 12 = ${(rate/12).toFixed(4)}%`,
          `Number of payments = ${term} years × 12 = ${term * 12}`,
          `Use the formula: M = P · r(1+r)^n / ((1+r)^n−1)`,
          `Monthly P&I ≈ <b>${fmt(calc.monthlyPI)}</b>. Add tax & insurance for total: <b>${fmt(calc.monthlyTotal)}</b>`
        ]} />}

        {/* Amortization table + chart — inside tool area */}
        {showResult && <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
          <div>
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <h3 className="font-bold text-slate-900">Amortization Schedule</h3>
              <div className="flex items-center gap-2 text-xs">
                <button onClick={()=>setScheduleMode('yearly')} data-testid="mort-yearly" className={`px-3 py-1 rounded ${scheduleMode==='yearly'?'bg-blue-700 text-white':'bg-slate-100 text-slate-700'}`}>Yearly</button>
                <button onClick={()=>setScheduleMode('monthly')} data-testid="mort-monthly" className={`px-3 py-1 rounded ${scheduleMode==='monthly'?'bg-blue-700 text-white':'bg-slate-100 text-slate-700'}`}>Monthly</button>
                <button onClick={()=>setShowAll(!showAll)} className="px-2 py-1 rounded border text-blue-700">{showAll?'Show less':'Show all'}</button>
              </div>
            </div>
            <div className="max-h-96 overflow-y-auto rounded border border-slate-200">
              {scheduleMode === 'yearly' ? (
                <table className="data-table !mb-0">
                  <thead className="sticky top-0 bg-blue-800 text-white"><tr><th className="px-2 py-1.5">Year</th><th className="px-2 py-1.5 text-right">Principal</th><th className="px-2 py-1.5 text-right">Interest</th><th className="px-2 py-1.5 text-right">Balance</th></tr></thead>
                  <tbody>
                    {(showAll ? calc.schedule : calc.schedule.slice(0, 10)).map((r,i) => (
                      <tr key={r.year} className={i%2?'bg-slate-50':''}><td className="px-2 py-1.5">{r.year}</td><td className="px-2 py-1.5 text-right">{fmt(r.principal)}</td><td className="px-2 py-1.5 text-right text-orange-600">{fmt(r.interest)}</td><td className="px-2 py-1.5 text-right font-semibold">{fmt(r.balance)}</td></tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <table className="data-table !mb-0 text-xs">
                  <thead className="sticky top-0 bg-blue-800 text-white"><tr><th className="px-2 py-1.5">Month</th><th className="px-2 py-1.5">Date</th><th className="px-2 py-1.5 text-right">Principal</th><th className="px-2 py-1.5 text-right">Interest</th><th className="px-2 py-1.5 text-right">Balance</th></tr></thead>
                  <tbody>
                    {(showAll ? calc.monthlySchedule : calc.monthlySchedule.slice(0, 24)).map((r,i) => (
                      <tr key={r.month} className={i%2?'bg-slate-50':''}><td className="px-2 py-1.5">{r.month}</td><td className="px-2 py-1.5">{r.date}</td><td className="px-2 py-1.5 text-right">{fmt(r.principal)}</td><td className="px-2 py-1.5 text-right text-orange-600">{fmt(r.interest)}</td><td className="px-2 py-1.5 text-right font-semibold">{fmt(r.balance)}</td></tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
          <div>
            <h3 className="font-bold text-slate-900 mb-3">Balance Over Time</h3>
            <svg viewBox="0 0 400 260" className="w-full">
              <line x1="40" y1="220" x2="390" y2="220" stroke="#cbd5e1" />
              <line x1="40" y1="20" x2="40" y2="220" stroke="#cbd5e1" />
              {(() => {
                const maxV = Math.max(calc.principal, ...calc.schedule.map(s => s.payment || 0));
                return [0, 0.25, 0.5, 0.75, 1].map((t, i) => (
                  <g key={i}>
                    <line x1="40" y1={220 - t * 200} x2="390" y2={220 - t * 200} stroke="#e2e8f0" strokeDasharray="3 3" />
                    <text x="36" y={220 - t * 200 + 4} textAnchor="end" fontSize="10" fill="#64748b">{'$' + Math.round(maxV * t / 1000) + 'K'}</text>
                  </g>
                ));
              })()}
              {(() => {
                const maxV = Math.max(calc.principal, ...calc.schedule.map(s => s.payment || 0));
                const bal = calc.schedule.map((s, i) => {
                  const x = 40 + (i / (calc.schedule.length - 1 || 1)) * 350;
                  const y = 220 - (s.balance / maxV) * 200;
                  return `${x},${y}`;
                }).join(' ');
                const intPts = calc.schedule.map((s, i) => {
                  const x = 40 + (i / (calc.schedule.length - 1 || 1)) * 350;
                  const y = 220 - ((s.cumInterest || 0) / maxV) * 200;
                  return `${x},${y}`;
                }).join(' ');
                const payPts = calc.schedule.map((s, i) => {
                  const x = 40 + (i / (calc.schedule.length - 1 || 1)) * 350;
                  const y = 220 - ((s.payment || 0) / maxV) * 200;
                  return `${x},${y}`;
                }).join(' ');
                return <>
                  <polyline points={bal} fill="none" stroke="#2563eb" strokeWidth="2.5" />
                  <polyline points={intPts} fill="none" stroke="#84cc16" strokeWidth="2.5" />
                  <polyline points={payPts} fill="none" stroke="#b91c1c" strokeWidth="2.5" />
                </>;
              })()}
              <text x="40" y="240" fontSize="10" fill="#64748b">Y1</text>
              <text x="370" y="240" fontSize="10" fill="#64748b">Y{term}</text>
            </svg>
            <div className="flex items-center gap-4 justify-center mt-2 text-xs">
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded" style={{background:'#2563eb'}}/>Balance</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded" style={{background:'#84cc16'}}/>Interest</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded" style={{background:'#b91c1c'}}/>Payment</span>
            </div>
            {showBiweekly && calc.biMonths > 0 && (() => {
              const savedMonths = Math.max(0, term * 12 - calc.biMonths);
              const savedYrs = Math.floor(savedMonths / 12);
              const savedMos = Math.round(savedMonths % 12);
              const biYrs = Math.floor(calc.biMonths / 12);
              const biMos = Math.round(calc.biMonths % 12);
              // Normalize any 12+ month overflow after rounding
              const norm = (y, m) => m >= 12 ? [y + Math.floor(m/12), m % 12] : [y, m];
              const [biY, biM] = norm(biYrs, biMos);
              const [savY, savM] = norm(savedYrs, savedMos);
              return (
                <div className="mt-4 border border-slate-200 rounded p-3 bg-blue-50/40" data-testid="mortgage-biweekly-result">
                  <div className="font-bold text-slate-900 mb-2">Biweekly Payback Results</div>
                  <table className="data-table !mb-0 text-sm">
                    <tbody>
                      <tr><td>Payoff time</td><td className="text-right font-semibold">{biY} yrs {biM} mo</td></tr>
                      <tr><td>Total interest</td><td className="text-right">{fmt(calc.biTotalInt)}</td></tr>
                      <tr><td>Interest saved vs. monthly</td><td className="text-right text-green-700 font-semibold">{fmt(Math.max(0, calc.totalInterest - calc.biTotalInt))}</td></tr>
                      <tr><td>Time saved</td><td className="text-right text-green-700 font-semibold">{savY} yrs {savM} mo</td></tr>
                    </tbody>
                  </table>
                </div>
              );
            })()}
          </div>
        </div>}
      </CalcShell>
    </>
  );
}
